import React, { useRef, useImperativeHandle } from "react";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import moment from "moment";

const getPrintTime = () => {
    const now = new Date();
    const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    };
    return now.toLocaleString("en-US", options);
};

const SalesReturnSlipTemplate = React.forwardRef(({ order, items, returnedItems, heading = "SALE RETURN SLIP" }, ref) => {

    const { CompanyName } = useSelector((state) => state.authReducer);

    // Calculate return totals from returned items
    const calculateReturnTotals = () => {
        let subtotal = 0;
        let totalGST = 0;
        let totalDiscount = 0;
        let netAmount = 0;

        returnedItems?.forEach(item => {
            if (item.ReturnQuantity > 0) {
                // Use ReturnAmount which is already calculated (ReturnQuantity * Price)
                const itemReturnAmount = parseFloat(item.ReturnAmount || 0);
                subtotal += itemReturnAmount;
                
                // Calculate GST if PriceWithoutGST is available
                if (item.PriceWithoutGST) {
                    const itemSubtotalWithoutGST = item.PriceWithoutGST * item.ReturnQuantity;
                    const itemGST = itemReturnAmount - itemSubtotalWithoutGST;
                    totalGST += itemGST > 0 ? itemGST : 0;
                } else if (item.PriceWithGST && item.PriceWithoutGST === undefined) {
                    // If only PriceWithGST is available, estimate GST (assuming standard rate)
                    // This is a fallback - ideally PriceWithoutGST should be available
                    const estimatedPriceWithoutGST = item.PriceWithGST / 1.18; // Assuming 18% GST
                    const itemSubtotalWithoutGST = estimatedPriceWithoutGST * item.ReturnQuantity;
                    const itemGST = itemReturnAmount - itemSubtotalWithoutGST;
                    totalGST += itemGST > 0 ? itemGST : 0;
                }
            }
        });

        // Apply discount if any (from order level discount)
        if (order?.DiscountPercent > 0) {
            totalDiscount = (subtotal * order.DiscountPercent) / 100;
        }

        netAmount = subtotal - totalDiscount;

        return {
            subtotal: subtotal.toFixed(2),
            totalGST: totalGST.toFixed(2),
            totalDiscount: totalDiscount.toFixed(2),
            netAmount: netAmount.toFixed(2)
        };
    };

    const returnTotals = calculateReturnTotals();

    // Format GST percentage if available
    const gstPercents = returnTotals.totalGST > 0 && returnTotals.subtotal > 0
        ? `(${((parseFloat(returnTotals.totalGST) / parseFloat(returnTotals.subtotal)) * 100).toFixed(2)}%)`
        : "";

    // Organize returned items into parent-child structure
    const organizeOrderItems = (items) => {
        // First get all parent items (DealItemId is null) that have ReturnQuantity > 0
        const parentItems = items.filter((item) => item.DealItemId === null && item.ReturnQuantity > 0);

        const result = [];

        for (const parent of parentItems) {
            // Find children - same RandomId AND DealItemId is NOT null
            const children = items.filter(
                (item) => item.RandomId === parent.RandomId && item.DealItemId !== null
            );

            result.push({
                parent,
                children,
            });
        }

        return result;
    };

    const organizedItems = organizeOrderItems(returnedItems || []);

    // Generate item rows with parent-child structure
    const itemBody = organizedItems?.map((group, groupIdx) => {
        const rows = [];

        // Parent item row - show ReturnQuantity instead of Quantity
        rows.push(
            <div
                key={`parent-${groupIdx}`}
                className="flex justify-between text-sm text-black px-1 py-0.5"
            >
                <span className="w-[20%] font-semibold">{group.parent.ReturnQuantity || group.parent.SaleReturnQty || 0}</span>
                <span className="w-[60%] font-medium text-left">
                    {group.parent.ProductName}
                    {group.parent.SizeName ? ` (${group.parent.SizeName})` : ""}
                    {group.parent.FlavourName ? ` ${group.parent.FlavourName}` : ""}
                    {group.parent.DiscountPercent > 0 && (
                        <span className="text-[10px] ml-1">
                            ({group.parent.DiscountPercent}% OFF)
                        </span>
                    )}
                </span>
                <span className="w-[20%] text-right font-semibold">
                    Rs {parseFloat(group.parent.ReturnAmount || 0).toFixed(2)}
                </span>
            </div>
        );

        // Child item rows
        if (group.children?.length > 0) {
            group.children.forEach((child, childIdx) => {
                rows.push(
                    <div
                        key={`child-${groupIdx}-${childIdx}`}
                        className="flex justify-between text-sm text-black px-1 py-0.5"
                    >
                        <span className="w-[20%]"></span>
                        <span className="w-[60%] pl-4 font-normal">
                            + {child.Quantity || 1} {child.ProductName}
                            {child.SizeName ? ` (${child.SizeName})` : ""}
                            {child.FlavourName ? ` ${child.FlavourName}` : ""}
                        </span>
                    </div>
                );
            });
        }

        return rows;
    }).flat();

    return (
        <div
            ref={ref}
            style={{
                fontFamily: "Calibri",
                fontSize: "12px",
                margin: "30px",
                display: "none"
            }}
            className="salesReturnSlip print-only"
        >
            <style>
                {`
          @media print {
            .print-only {
              display: block !important;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            .print-only {
              display: none !important;
            }
          }
        `}
            </style>

            <table style={{ width: "100%" }}>
                <thead>
                    <tr>
                        <th style={{
                            textAlign: "center",
                            marginBottom: 0,
                            fontSize: "24px",
                            fontFamily: "Calibri",
                            fontWeight: 600,
                            color: "#000",
                            height: "24px"
                        }}>
                            {CompanyName}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th style={{
                            textAlign: "center",
                            fontSize: "11px",
                            fontWeight: 400,
                            border: 0,
                            color: "#000"
                        }}>
                            {order?.branchName || order?.BranchName || "Branch Address"}
                        </th>
                        <th style={{
                            textAlign: "center",
                            fontSize: "11px",
                            fontWeight: 400,
                            border: 0,
                            color: "#000"
                        }}>
                            {order?.AreaName || "Branch Address"}
                        </th>
                    </tr>
                </tbody>
            </table>

            <div className="my-1 border-t-2 border-b-2 border-black text-center w-full">
                {/* Heading */}
                <div className="w-full text-center font-semibold text-black text-sm border-b-2 border-black bg-red-50">
                    {heading}
                </div>

                {/* Order Mode Row */}
                <div className="flex justify-between text-sm text-black font-medium px-1">
                    <strong>Order Mode</strong>
                    <span>{order?.OrderMode || order?.OrderModeName || "N/A"}</span>
                </div>

                {/* Customer Details */}
                <div className="w-full flex flex-col mt-1 text-left text-sm text-black px-1 ">
                    <div>
                        <strong>Customer:</strong> &nbsp; {order?.CustomerName || order?.customerName || "N/A"}
                    </div>
                    <div>
                        <strong>Address:</strong>&nbsp;  {order?.CustomerAddress || order?.address || order?.CompleteAddress || "N/A"}
                    </div>
                    <div>
                        <strong>Phone:</strong> &nbsp; {order?.PhoneNumber || order?.phoneNumber || "N/A"}
                    </div>
                    {order?.AlternateNumber && (
                        <div>
                            <strong>Alt:</strong>&nbsp;  {order?.AlternateNumber}
                        </div>
                    )}
                    {order?.LandMark && (
                        <div>
                            <strong>LandMark:</strong>&nbsp;  {order?.LandMark}
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full text-sm text-black px-1 ">
                <div className="flex justify-between font-medium">
                    <span>Order #</span>
                    <span>{order?.OrderNumber || order?.orderNumber || "N/A"}</span>
                </div>
                {order?.SalesReturnNumber && (
                    <div className="flex justify-between font-medium text-red-600">
                        <span>Sales Return #</span>
                        <span>{order.SalesReturnNumber}</span>
                    </div>
                )}
                <div className="flex justify-between font-medium">
                    <span>Return Date/Time:</span>
                    <span>{order?.ReturnDate ? moment(order.ReturnDate).format("YYYY-MM-DD HH:mm:ss") : getPrintTime()}</span>
                </div>
                <div className="flex justify-between font-medium text-red-600">
                    <span>Status:</span>
                    <span>RETURNED</span>
                </div>
            </div>

            <div className="w-full border-y-2 border-black my-1 px-1 py-1">
                <div className="flex justify-between font-medium text-black text-sm">
                    <span className="w-[20%]">Qty</span>
                    <span className="w-[60%] text-left">Item</span>
                    <span className="w-[20%] text-right">Amount</span>
                </div>

                <div>{itemBody}</div>
            </div>

            <div className="w-full flex flex-col text-black text-sm">
                {/* Row: Subtotal */}
                {parseFloat(returnTotals.subtotal) > 0 && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Subtotal:</span>
                        <span className="font-normal">Rs {returnTotals.subtotal}</span>
                    </div>
                )}

                {/* Row: GST */}
                {parseFloat(returnTotals.totalGST) > 0 && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Gst {gstPercents}</span>
                        <span className="font-normal">Rs {returnTotals.totalGST}</span>
                    </div>
                )}

                {/* Row: Discount */}
                {parseFloat(returnTotals.totalDiscount) > 0 && (
                    <div className="flex justify-between items-center py-0">
                        <span className="font-normal">Discount</span>
                        <span className="font-normal">Rs {returnTotals.totalDiscount}</span>
                    </div>
                )}

                {/* Row: Net Return Amount (always shown) */}
                <div className="flex justify-between items-center py-0">
                    <span className="font-medium">Net Return Amount:</span>
                    <span className="font-medium">Rs {returnTotals.netAmount}</span>
                </div>
            </div>

            <br />

            <div className="flex flex-col w-full items-center text-black">
                <span className="italic text-red-600 font-semibold">RETURN PROCESSED</span>
                <span className="italic">Powered by EatX</span>
                <span> Printed on: {getPrintTime()}</span>
            </div>
        </div>
    );
});

const SalesReturnSlip = React.forwardRef(({ orderDetails, orderItems, returnedItems, onAfterPrint, heading = "SALE RETURN SLIP" }, ref) => {

    const componentRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `SalesReturnSlip_${orderDetails?.SalesReturnNumber || orderDetails?.OrderNumber || "unknown"}`,
        pageStyle: "@page { size: auto; margin: 0mm; }",
        onAfterPrint: onAfterPrint || (() => { })
    });

    // Expose print function via ref
    useImperativeHandle(ref, () => ({
        print: () => {
            if (orderDetails && returnedItems && returnedItems.length > 0) {
                // Use requestAnimationFrame to maintain user action context
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        try {
                            handlePrint();
                        } catch (error) {
                            console.error("Error in SalesReturnSlip print:", error);
                        }
                    }, 100);
                });
            }
        }
    }));

    return (
        <div>
            {/* Hidden sales return slip template - only visible when printing */}
            <SalesReturnSlipTemplate ref={componentRef} order={orderDetails} items={orderItems} returnedItems={returnedItems} heading={heading} />
        </div>
    );
});

export default SalesReturnSlip;

